import FileUploadIcon from "@mui/icons-material/FileUpload";
import {useSelector} from "react-redux";
import React, {useEffect, useState, useRef} from "react";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import VLFModal from "@/components/Modal";

import {CompetitionSeasonAPI} from "@/api/competitionSeason";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {SUCCESSFUL_OPERATION, ERROR_OPERATION} from "@/utils/constants";
import {AREAS, canWrite} from "@/utils/access";

import "./index.scss";

function CompetitionSeasonsPage(props) {
	const {handleNotification} = props;
	const seasonId = useSelector((state) => state?.season?.id);
	const user = useSelector((state) => state?.user);
	// Un solo indirizzo: chi gestisce le configurazioni modifica, gli altri consultano
	const operational = !canWrite(user, AREAS.CONFIGURAZIONI);
	const editionBase = "/competitions";
	const requestNumber = useRef(0);

	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [competitionSeasons, setCompetitionSeasons] = useState([]);

	const [id, setId] = useState();
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);

	const getCompetitionSeasons = async () => {
		const request = ++requestNumber.current;
		setLoading(true);
		try {
			const response = operational
				? seasonId
					? await CompetitionSeasonAPI.listForCurrentUser({seasonId})
					: []
				: await CompetitionSeasonAPI.list();
			if (request !== requestNumber.current) return;
			setCompetitionSeasons(
				(response || [])
					.slice()
					.sort(
						(a, b) =>
							(b.season?.name || "").localeCompare(a.season?.name || "") ||
							(a.competition?.name || "").localeCompare(
								b.competition?.name || "",
							),
					),
			);
		} catch (error) {
			if (request === requestNumber.current)
				handleNotification(
					error.response?.data?.error || ERROR_OPERATION,
					"error",
				);
		} finally {
			if (request === requestNumber.current) setLoading(false);
		}
	};

	const deleteCompetitionSeason = async () => {
		setLoading(true);
		try {
			await CompetitionSeasonAPI.delete(id);
			getCompetitionSeasons();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(
				error.response?.data?.error || ERROR_OPERATION,
				"error",
			);
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	useEffect(() => {
		getCompetitionSeasons();
		return () => {
			requestNumber.current++;
		};
	}, [operational, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const getLabel = (cs) =>
		(cs.competition?.name || "") + " - " + (cs.season?.name || "");

	const listConfiguration = {
		data: competitionSeasons,
		imageUrl: () => require("@/assets/images/coach/match.png"),
		title: (cs) => getLabel(cs),
		subtitle: (cs) => cs.competition?.competitionType,
		onClick: (cs) => navigate(editionBase + "/" + cs.id),
		actions: operational
			? []
			: [
					{
						icon: <EditIcon />,
						variant: "outlined",
						title: "Modifica",
						onClick: (cs) => navigate(editionBase + "/" + cs.id + "/edit"),
					},
					{
						icon: <DeleteIcon />,
						variant: "outlined",
						title: "Elimina",
						onClick: (cs) => {
							setId(cs.id);
							setOpenHandleConfirm(true);
						},
						color: "error",
					},
				],
	};

	const columns = [
		{
			label: "Competizione",
			key: "competition",
			align: "start",
			customRender: (item) => item.competition?.name,
		},
		{
			label: "Stagione",
			key: "season",
			align: "start",
			customRender: (item) => item.season?.name,
		},
		{
			label: "Tipologia",
			key: "type",
			align: "start",
			customRender: (item) => item.competition?.competitionType,
		},
	];

	const headerActions = operational
		? []
		: [
				{
					icon: <FileUploadIcon />,
					title: "Importa JSON",
					onClick: () => navigate("/settings/competition-seasons/import"),
				},
				{
					icon: <AddIcon />,
					title: "Aggiungi",
					onClick: () => navigate("/settings/competition-seasons/new"),
				},
			];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Competizioni"
				actions={<MainActions actions={headerActions} />}
			/>
			{operational && !loading && !competitionSeasons.length && (
				<p>
					{seasonId
						? "Nessuna competizione con le tue squadre in questa stagione."
						: "Seleziona una stagione."}
				</p>
			)}
			<CardsContainer
				tableComponent={
					<CardsTable
						breadcrumbs
						configurationColumns={columns}
						data={competitionSeasons}
						fullWidth
						rowActions
						handleClick={(item) => navigate(editionBase + "/" + item.id)}
						handleEdit={
							operational
								? undefined
								: (item) => navigate(editionBase + "/" + item.id + "/edit")
						}
						handleDelete={
							operational
								? undefined
								: (item) => {
										setId(item.id);
										setOpenHandleConfirm(true);
									}
						}
						defaultSortedField="competition"
					/>
				}
				listComponent={
					<CardsList
						breadcrumbs
						listConfiguration={listConfiguration}
						defaultSortedField="competition"
					/>
				}
			/>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questa edizione?
					</div>
				}
				confirm={deleteCompetitionSeason}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
		</Page>
	);
}

export default CompetitionSeasonsPage;
